import React, { useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import DestinationBrandlive from '../../../images/brandlive-platform-icon.png';
import TellyCard from '../../../images/telly-card.png';
import Socket from '../../../connection/socket';
import { GreenroomState as GS } from '../../../stores/types/types';
import { APP } from '../../../stores/session-status';
import { Destination } from '../../../stores/types/broadcasting';
import * as BroadcastActions from '../../../stores/actions/broadcasting';
import { cardBackground, upcomingDateTime } from '../../../utils/utils';
import Switch from '../../general-components/switch';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import BrandLiveLogo from '../../general-components/brandlive-logo';
import type { Meeting } from '../../../stores/types/session';
import { MODAL_TEMPLATES } from '../../../stores/reducers/modal';
import DestinationCard from './destination-card';
import { MAX_TOGGLED } from './destination-count';

interface Props {
	socket: Socket | null;
	meeting: Meeting;
	location: string;
}

export enum StreamSettingLocation {
	MetaMenu = 'meta-menu',
	ShowDetails = 'show-details',
	StreamModal = 'stream-modal',
}

export default function StreamingSettings(props: Props) {
	const {
		meeting,
		location,
	} = props;

	const dispatch = useDispatch();

	const token = useSelector((s: GS) => s.SessionReducer.token);
	const appSignedInWith = useSelector((s: GS) => s.SessionReducer.appSignedInWith);
	const goLiveButtonClicked = useSelector((s: GS) => s.BroadcastReducer.goLiveButtonClicked);
	const addingDestination = useSelector((s: GS) => s.BroadcastReducer.addingDestination);
	const editingDestination = useSelector((s: GS) => s.BroadcastReducer.editingDestination);
	const destinations = useSelector((s: GS) => s.BroadcastReducer.destinations);
	const modalOpen = useSelector((s: GS) => s.ModalReducer.modalOpen);
	const modalTemplate = useSelector((s: GS) => s.ModalReducer.modalTemplate);

	const destinationsRef = useRef<HTMLDivElement>(null);

	const isMetaMenuWithStreamModalOpen = useMemo(
		() => (
			modalOpen && modalTemplate === MODAL_TEMPLATES.LIVE_STREAM_CONFIRMATION
			&& location === StreamSettingLocation.MetaMenu
		),
		[location, modalOpen, modalTemplate],
	);

	const defaultDestination = useMemo(() => destinations.find(({ id, isBlp }) => isBlp ?? !id), [destinations]);

	const defaultToggled = useMemo(
		() => (!!defaultDestination?.selected),
		[defaultDestination],
	);

	const maxToggled = useMemo(
		() => destinations.filter(({ selected: s }) => s).length >= MAX_TOGGLED,
		[destinations],
	);

	function toggleDefault() {
		if (!token || !meeting) return;

		if (defaultDestination) {
			dispatch(BroadcastActions.updateDestination(
				token,
				meeting.meeting,
				{ ...defaultDestination, selected: !defaultDestination.selected },
			));
		}
	}

	const destinationCards = useMemo(() => {
		const filteredDestinations = location === StreamSettingLocation.ShowDetails
			? destinations.filter((destination) => !(destination.isBlp ?? !destination.id))
			: destinations;

		return filteredDestinations.map(
			(destination: Destination, index: number) => (
				<DestinationCard
					key={`${index}-${destination.id ?? 0}`}
					meetingId={meeting.meeting}
					location={location}
					goLiveButtonClicked={goLiveButtonClicked}
					destination={destination}
					maxSelected={maxToggled}
					isEditing={
						editingDestination === String(destination.id ?? 0) && !isMetaMenuWithStreamModalOpen
					}
				/>
			),
		);
	}, [
		destinations,
		editingDestination,
		goLiveButtonClicked,
		isMetaMenuWithStreamModalOpen,
		location,
	]);

	return (
		<>
			{location === StreamSettingLocation.ShowDetails && appSignedInWith !== APP.GREENROOM && (
				<div className={classNames('main-destination', { disabled: goLiveButtonClicked })}>
					<div className="destination-header">
						<div className="destination-label">
							<img src={DestinationBrandlive} alt={DestinationBrandlive} />
							<BrandLiveLogo height={18} width={50} marginTop={-1} />
						</div>
						<div className="destination-controls">
							<Switch
								on={defaultToggled}
								onChange={toggleDefault}
								className={`green ${maxToggled && !defaultToggled ? 'disabled' : ''}`}
							/>
						</div>
					</div>
					<div className="main-destination-meta">
						<div className="main-destination-image" style={cardBackground(meeting, TellyCard, false, true)} />
						<div className="main-destination-info">
							<label>
								{upcomingDateTime(
									new Date(meeting.start_time).getTime(),
									new Date(meeting.end_time).getTime(),
									true,
									true,
								)}
							</label>
							<span>{meeting.title}</span>
							<div className="destination-status">
								<Icon name={ICONS.VIEWERS_EYE} color={COLORS.GRAY} size={11} />
								<label>Broadcast</label>
							</div>
						</div>
					</div>
				</div>
			)}
			<div className="custom-destinations" ref={destinationsRef}>
				{destinationCards}
				{addingDestination && !isMetaMenuWithStreamModalOpen && (
					<DestinationCard
						meetingId={meeting.meeting}
						location={location}
						goLiveButtonClicked={false}
						destination={addingDestination}
						maxSelected={maxToggled}
						isEditing={!editingDestination}
						isNew={true}
					/>
				)}
			</div>
		</>
	);
}
